import { api } from '../../lib/api';

export interface AccountDTO {
  id: number;
  code: string;
  name: string;
  type: 'asset' | 'liability' | 'equity' | 'income' | 'expense';
  parentId: number | null;
  isActive: boolean;
}

export async function listAccounts(): Promise<AccountDTO[]> {
  const { data } = await api.get<AccountDTO[]>('/api/accounting/accounts');
  return data;
}

export async function createAccount(body: Pick<AccountDTO, 'code' | 'name' | 'type'> & { parentId?: number | null }): Promise<AccountDTO> {
  const { data } = await api.post<AccountDTO>('/api/accounting/accounts', body);
  return data;
}

export async function updateAccount(id: number, body: Partial<AccountDTO>): Promise<AccountDTO> {
  const { data } = await api.patch<AccountDTO>(`/api/accounting/accounts/${id}`, body);
  return data;
}

export async function deleteAccount(id: number): Promise<void> {
  await api.delete(`/api/accounting/accounts/${id}`);
}

interface DateRangeQuery { from?: string; to?: string }

export interface BalanceSheetDTO {
  range: { from: string; to: string };
  assets: AccountBalanceLine[];
  liabilities: AccountBalanceLine[];
  equity: AccountBalanceLine[];
  totals: { assets: number; liabilities: number; equity: number };
  netIncome: number;
}

export interface AccountBalanceLine {
  accountId: number;
  code: string;
  name: string;
  type: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface IncomeStatementDTO {
  range: { from: string; to: string };
  income: AccountBalanceLine[];
  expenses: AccountBalanceLine[];
  totals: { income: number; expenses: number; netIncome: number };
}

export interface LedgerLineDTO {
  date: string;
  entryNumber: string;
  description: string;
  accountCode: string;
  accountName: string;
  debit: number;
  credit: number;
}

export interface AccountLedgerDTO {
  accountId: number;
  code: string;
  name: string;
  type: string;
  openingBalance: number;
  movements: Array<LedgerLineDTO & { balance: number }>;
  closingBalance: number;
}

export async function balanceSheet(range: DateRangeQuery): Promise<BalanceSheetDTO> {
  const { data } = await api.get<BalanceSheetDTO>('/api/accounting/reports/balance-sheet', { params: range });
  return data;
}

export async function incomeStatement(range: DateRangeQuery): Promise<IncomeStatementDTO> {
  const { data } = await api.get<IncomeStatementDTO>('/api/accounting/reports/income-statement', { params: range });
  return data;
}

export async function generalLedger(range: DateRangeQuery): Promise<LedgerLineDTO[]> {
  const { data } = await api.get<LedgerLineDTO[]>('/api/accounting/reports/general-ledger', { params: range });
  return data;
}

export async function accountLedger(accountId: number, range: DateRangeQuery): Promise<AccountLedgerDTO> {
  const { data } = await api.get<AccountLedgerDTO>(`/api/accounting/reports/account-ledger/${accountId}`, { params: range });
  return data;
}

export interface SalesReportDTO {
  range: { from: string; to: string };
  invoiceCount: number;
  totalGross: number;
  totalNet: number;
  totalTax: number;
  byCashier: Array<{ cashierId: number; cashierName: string; count: number; total: number }>;
  byMethod: Array<{ method: string; count: number; total: number }>;
  rows: Array<{
    invoiceId: number;
    number: string;
    issuedAt: string;
    status: string;
    cashierName: string;
    customerName: string;
    subtotal: number;
    taxTotal: number;
    total: number;
    paymentMethods: string[];
  }>;
}

export async function salesReport(
  range: DateRangeQuery & { cashierId?: number; status?: string },
): Promise<SalesReportDTO> {
  const { data } = await api.get<SalesReportDTO>('/api/accounting/reports/sales', { params: range });
  return data;
}

export function xlsxUrl(
  report: 'balance-sheet' | 'income-statement' | 'general-ledger' | 'sales',
  range: DateRangeQuery & Record<string, string | number | undefined>,
): string {
  const flat: Record<string, string> = { format: 'xlsx' };
  for (const [k, v] of Object.entries(range)) {
    if (v !== undefined && v !== null && v !== '') flat[k] = String(v);
  }
  return `/api/accounting/reports/${report}?${new URLSearchParams(flat).toString()}`;
}
